/**
 * Collection & Request API Client
 *
 * Handles all collection and request-related API calls.
 * Backend wraps responses in { success: true, data: T } — we unwrap here.
 */

import { AppConfig } from '../auth/config';

export interface ApiCollection {
  id: string;
  name: string;
  description?: string;
  sortOrder?: number;
  createdAt?: string;
  updatedAt?: string;
  folders?: ApiFolder[];
  requests?: ApiCollectionRequest[];
}

export interface ApiFolder {
  id: string;
  name: string;
  parentId?: string;
  sortOrder?: number;
}

export interface ApiCollectionRequest {
  id: string;
  name: string;
  method: string;
  url?: string;
  folderId?: string;
  sortOrder?: number;
}

export interface ApiFullRequest {
  id: string;
  collectionId: string;
  folderId?: string;
  name: string;
  method: string;
  url: string;
  headers: Array<{ key: string; value: string; description?: string; enabled: boolean }>;
  params: Array<{ key: string; value: string; description?: string; enabled: boolean }>;
  body?: {
    type: 'none' | 'raw' | 'form-data' | 'x-www-form-urlencoded' | 'binary';
    raw?: { content: string; contentType: 'json' | 'xml' | 'text' | 'html' | 'javascript' };
    formData?: Array<{ key: string; value: string; description?: string; enabled: boolean }>;
    urlencoded?: Array<{ key: string; value: string; description?: string; enabled: boolean }>;
  };
  auth?: {
    type: string;
    bearer?: { token: string };
    basic?: { username: string; password: string };
    apiKey?: { key: string; value: string; addTo: 'header' | 'query' };
    hawk?: { authId: string; authKey: string; algorithm: 'sha256' | 'sha1' };
  };
  scripts?: {
    preRequest?: string;
    postRequest?: string;
  };
  sortOrder?: number;
  createdAt?: string;
  updatedAt?: string;
}

export interface CreateCollectionRequest {
  name: string;
  description?: string;
}

export interface UpdateCollectionRequest {
  name?: string;
  description?: string;
}

export interface CreateRequestPayload {
  name: string;
  method: string;
  url?: string;
  folderId?: string;
  headers?: Array<{ key: string; value: string; description?: string; enabled: boolean }>;
  params?: Array<{ key: string; value: string; description?: string; enabled: boolean }>;
  body?: ApiFullRequest['body'];
  auth?: ApiFullRequest['auth'];
  scripts?: ApiFullRequest['scripts'];
}

export interface UpdateRequestPayload {
  name?: string;
  method?: string;
  url?: string;
  folderId?: string;
  headers?: Array<{ key: string; value: string; description?: string; enabled: boolean }>;
  params?: Array<{ key: string; value: string; description?: string; enabled: boolean }>;
  body?: ApiFullRequest['body'];
  auth?: ApiFullRequest['auth'];
  scripts?: ApiFullRequest['scripts'];
}

/**
 * Create the collection/request API client
 */
export function createCollectionApi(config: AppConfig | null, getAccessToken: () => Promise<string | null>) {
  const baseUrl = config?.api?.endpoint || '';

  async function fetchWithAuth<T>(path: string, options: RequestInit = {}): Promise<T> {
    const token = await getAccessToken();
    if (!token) {
      throw new Error('Not authenticated');
    }

    const response = await fetch(`${baseUrl}${path}`, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
        ...options.headers,
      },
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: response.statusText }));
      throw new Error(error.error?.message || error.message || `Request failed: ${response.status}`);
    }

    // Handle 204 No Content
    if (response.status === 204) {
      return null as T;
    }

    const result = await response.json();
    // Unwrap the { success: true, data: T } envelope
    return result.data !== undefined ? result.data : result;
  }

  return {
    // Collections
    async listCollections(workspaceId: string): Promise<ApiCollection[]> {
      return fetchWithAuth(`/workspaces/${workspaceId}/collections`);
    },

    async getCollection(id: string): Promise<ApiCollection> {
      return fetchWithAuth(`/collections/${id}`);
    },

    async createCollection(workspaceId: string, data: CreateCollectionRequest): Promise<ApiCollection> {
      return fetchWithAuth(`/workspaces/${workspaceId}/collections`, {
        method: 'POST',
        body: JSON.stringify(data),
      });
    },

    async updateCollection(id: string, data: UpdateCollectionRequest): Promise<ApiCollection> {
      return fetchWithAuth(`/collections/${id}`, {
        method: 'PUT',
        body: JSON.stringify(data),
      });
    },

    async deleteCollection(id: string): Promise<void> {
      await fetchWithAuth(`/collections/${id}`, { method: 'DELETE' });
    },

    // Requests
    async createRequest(collectionId: string, data: CreateRequestPayload): Promise<ApiFullRequest> {
      return fetchWithAuth(`/collections/${collectionId}/requests`, {
        method: 'POST',
        body: JSON.stringify(data),
      });
    },

    async getRequest(id: string): Promise<ApiFullRequest> {
      return fetchWithAuth(`/requests/${id}`);
    },

    async updateRequest(id: string, data: UpdateRequestPayload): Promise<ApiFullRequest> {
      return fetchWithAuth(`/requests/${id}`, {
        method: 'PUT',
        body: JSON.stringify(data),
      });
    },

    async deleteRequest(id: string): Promise<void> {
      await fetchWithAuth(`/requests/${id}`, { method: 'DELETE' });
    },
  };
}

export type CollectionApi = ReturnType<typeof createCollectionApi>;
